import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { Cancel, CheckCircle, Info } from '@mui/icons-material';

import { LoadingSpinner } from './LoadingSpinner';
import { formatOfficialDateBR } from '../utils/officialDate';

export interface MonitoringActivationEvent {
  id: number;
  action: 'activated' | 'deactivated';
  timestamp: string;
  user?: string;
  entity_label?: string;
  assunto_label?: string;
  agency_name?: string;
  details?: string;
}

interface MonitoringActivationLogListProps {
  history: MonitoringActivationEvent[];
  loading?: boolean;
  isActive?: boolean;
  activeEntityLabel?: string | null;
  activeStatusMessage?: string | null;
}

function resolveEntityLabel(event: MonitoringActivationEvent, fallback?: string | null): string {
  return event.entity_label ?? event.assunto_label ?? event.agency_name ?? fallback ?? 'Item';
}

/** Lista reutilizável de ativação/desativação (assuntos e agências). */
export function MonitoringActivationLogList({
  history,
  loading = false,
  isActive,
  activeEntityLabel,
  activeStatusMessage,
}: MonitoringActivationLogListProps) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <LoadingSpinner message="Carregando histórico…" />
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">Nenhum evento registrado.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          Total de <strong>{history.length}</strong> eventos registrados
        </Typography>
      </Alert>
      <List>
        {history.map((event, index) => {
          const isActivation = event.action === 'activated';
          const label = resolveEntityLabel(event, activeEntityLabel);
          return (
            <React.Fragment key={event.id}>
              <ListItem sx={{ alignItems: 'flex-start', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: isActivation ? 'success.main' : 'error.main' }}>
                  {isActivation ? <CheckCircle /> : <Cancel />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {label} — {isActivation ? 'Ativado' : 'Desativado'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por <strong>{event.user ?? 'Usuário'}</strong> em{' '}
                    <strong>{formatOfficialDateBR(event.timestamp)}</strong>
                  </Typography>
                  {event.details && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {event.details}
                    </Typography>
                  )}
                </Box>
              </ListItem>
              {index < history.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
      {isActive && activeStatusMessage && (
        <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mt: 1 }}>
          {activeStatusMessage}
        </Typography>
      )}
    </>
  );
}
